import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MAX_ATTEMPTS = 8; // ~16s of polling (8 * 2s)
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const { setUser } = useAuth();
  const [isPlan, setIsPlan] = useState(false);
  const [status, setStatus] = useState({ state: "pending", message: "Confirming your payment…" });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    cancelledRef.current = false;
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session reference. If you were charged, contact support." });
      return;
    }

    const poll = async () => {
      if (cancelledRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        const ps = (data.payment_status || "").toLowerCase();
        const st = (data.status || "").toLowerCase();
        const plan = (data.product_slug || "").startsWith("plan_");
        setIsPlan(plan);
        if (plan && ps === "paid" && data.granted) {
          const me = await api.get("/auth/me");
          setUser(me.data);
          setStatus({ state: "success", message: `Payment confirmed. ${me.data.plan_info?.label || "Your plan"} is now active.` });
          setTimeout(() => nav("/plans", { replace: true }), 1500);
          return;
        }

        if (ps === "paid" && data.granted) {
          setStatus({ state: "success", message: "Payment confirmed. Redirecting to your assessment…" });
          setTimeout(() => nav("/trca", { replace: true }), 1200);
          return;
        }
        if (st === "expired") {
          setStatus({ state: "expired", message: "This checkout session expired. Please try again." });
          setTimeout(() => nav(plan ? "/plans" : "/trca-qualifier", { replace: true }), 1500);
          return;
        }

        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({
            state: "timeout",
            message: "We are still processing your payment. You will receive an email when it is confirmed.",
          });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_MS);
      } catch (ex) {
        setStatus({ state: "error", message: formatApiErrorDetail(ex.response?.data?.detail) || ex.message });
      }
    };

    poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, nav, setUser, round]);

  const ICONS = {
    pending: <Loader2 className="w-10 h-10 text-brand animate-spin" />,
    success: <CheckCircle2 className="w-10 h-10 text-good" />,
    expired: <XCircle className="w-10 h-10 text-warn" />,
    timeout: <Loader2 className="w-10 h-10 text-ink-muted" />,
    error: <XCircle className="w-10 h-10 text-warn" />,
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white border border-ink-line rounded-sm p-8 text-center">
        <div className="flex justify-center mb-5">{ICONS[status.state]}</div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">{isPlan ? "Payment · TWIN Plan" : "Payment · TRCA Assessment"}</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-2">
          {status.state === "success" ? "You're in." : status.state === "pending" ? "Hang tight…" : status.state === "expired" ? "Session expired" : status.state === "timeout" ? "Still confirming…" : "Something went wrong"}
        </h1>
        <p className="text-sm text-ink-muted mt-3 leading-relaxed" data-testid="payment-status-message">{status.message}</p>

        {status.state === "timeout" && (
          <div className="mt-6 space-y-2">
            <button
              onClick={() => { attemptsRef.current = 0; setStatus({ state: "pending", message: "Re-checking…" }); setRound((r) => r + 1); }}
              className="w-full px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors"
              data-testid="payment-recheck-btn"
            >
              Re-check status
            </button>
            <Link to="/dashboard" className="block text-xs text-ink-muted hover:text-ink underline-offset-4 hover:underline">
              Back to dashboard
            </Link>
          </div>
        )}
        {status.state === "error" && (
          <div className="mt-6">
            <Link to={isPlan ? "/plans" : "/trca-qualifier"} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm transition-colors">
              Try again <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
